<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useEngraverStore } from '@/stores/engraver'
import { useSupportStore } from '@/stores/support'

// Opened from the nav menu (and from the link in a reply email). The team
// answers from a Slack thread; replies land here live, or wait as unread.
const route = useRoute()
const authStore = useAuthStore()
const engraverStore = useEngraverStore()
const support = useSupportStore()

const draft = ref('')
const sending = ref(false)
const error = ref('')
const scroller = ref<HTMLElement | null>(null)
const input = ref<HTMLTextAreaElement | null>(null)

const pageLabel = computed(() => {
  const t = route.meta?.title
  if (typeof t === 'string' && t) return t
  if (route.name) return String(route.name)
  return route.path
})

const messages = computed(() => support.active?.messages ?? [])

function scrollToBottom() {
  nextTick(() => {
    if (scroller.value) scroller.value.scrollTop = scroller.value.scrollHeight
  })
}

watch(() => messages.value.length, scrollToBottom)
watch(() => support.active?.id, scrollToBottom)
watch(() => support.panelOpen, (open) => {
  if (open) {
    error.value = ''
    scrollToBottom()
    nextTick(() => input.value?.focus())
  }
})

async function send() {
  const text = draft.value.trim()
  if (!text || sending.value) return
  sending.value = true
  error.value = ''
  try {
    const engraver = engraverStore.selectedEngraver
    await support.send(text, {
      page: pageLabel.value,
      url: typeof window !== 'undefined' ? window.location.href : '',
      context: engraver
        ? {
            engraver: engraver.name,
            status: engraver.status,
            firmware: (engraver as any).firmware_version,
          }
        : {},
    })
    draft.value = ''
  } catch (e: any) {
    error.value = e?.message || 'Could not send. Please try again.'
  } finally {
    sending.value = false
    nextTick(() => input.value?.focus())
  }
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
    e.preventDefault()
    send()
  }
}

function time(iso: string) {
  const d = new Date(iso)
  const sameDay = d.toDateString() === new Date().toDateString()
  return sameDay
    ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ', ' +
      d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
}

const awaitingReply = computed(() => {
  const m = messages.value
  return m.length > 0 && m[m.length - 1].sender === 'customer'
})
</script>

<template>
  <transition
    enter-active-class="transition ease-out duration-150"
    enter-from-class="opacity-0 translate-y-2"
    enter-to-class="opacity-100 translate-y-0"
    leave-active-class="transition ease-in duration-100"
    leave-from-class="opacity-100 translate-y-0"
    leave-to-class="opacity-0 translate-y-2"
  >
    <div
      v-if="support.panelOpen"
      class="fixed bottom-5 right-5 z-50 print:hidden w-96 max-w-[calc(100vw-2.5rem)]
             h-[32rem] max-h-[calc(100vh-6rem)] flex flex-col
             rounded-2xl bg-white shadow-2xl border border-gray-200 overflow-hidden"
      role="dialog"
      aria-label="Support chat"
    >
      <!-- Header -->
      <div class="bg-gray-900 text-white px-4 py-3 flex items-center gap-2 shrink-0">
        <span class="font-semibold text-sm flex-1">
          {{ support.showHistory ? 'Your conversations' : 'Support' }}
        </span>
        <button
          v-if="!support.showHistory && support.conversations.length > 0"
          type="button"
          @click="support.showHistory = true"
          class="text-xs text-white/70 hover:text-white"
        >History</button>
        <button
          v-if="support.showHistory || support.active"
          type="button"
          @click="support.newConversation()"
          class="text-xs text-white/70 hover:text-white"
        >New</button>
        <button type="button" @click="support.closePanel()" class="ml-1 text-white/70 hover:text-white" aria-label="Close">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- History -->
      <div v-if="support.showHistory" class="flex-1 overflow-y-auto divide-y divide-gray-100">
        <button
          v-for="c in support.conversations"
          :key="c.id"
          type="button"
          @click="support.openConversation(c.id)"
          class="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-start gap-2"
        >
          <span class="mt-1.5 w-2 h-2 rounded-full shrink-0" :class="c.unread ? 'bg-red-500' : 'bg-transparent'" />
          <span class="flex-1 min-w-0">
            <span class="block text-sm text-gray-900 truncate">{{ c.preview || 'Conversation' }}</span>
            <span class="block text-xs text-gray-400">{{ time(c.last_message_at) }}</span>
          </span>
        </button>
      </div>

      <!-- Conversation -->
      <template v-else>
        <div ref="scroller" class="flex-1 overflow-y-auto px-4 py-3 space-y-2 bg-gray-50">
          <div v-if="support.loading" class="text-center text-xs text-gray-400 py-6">Loading…</div>

          <div v-else-if="!support.active" class="py-6 text-center">
            <p class="text-sm font-semibold text-gray-900">How can we help?</p>
            <p class="text-sm text-gray-500 mt-1">
              Someone on our team will reply right here. We can see who you are and the page you're on.
            </p>
          </div>

          <template v-else>
            <div
              v-for="m in messages"
              :key="m.id"
              class="flex"
              :class="m.sender === 'customer' ? 'justify-end' : 'justify-start'"
            >
              <div class="max-w-[85%]">
                <div
                  class="px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words"
                  :class="m.sender === 'customer'
                    ? 'bg-gray-900 text-white rounded-br-md'
                    : 'bg-white text-gray-900 border border-gray-200 rounded-bl-md'"
                >{{ m.body }}</div>
                <div
                  class="text-[11px] text-gray-400 mt-0.5 px-1"
                  :class="m.sender === 'customer' ? 'text-right' : 'text-left'"
                >
                  <span v-if="m.sender === 'staff'">{{ m.author_name || 'Support' }} · </span>{{ time(m.created_at) }}
                </div>
              </div>
            </div>
            <p v-if="awaitingReply" class="text-center text-xs text-gray-400 pt-2">
              We'll reply here. If you close this, we'll email you when we do.
            </p>
          </template>
        </div>

        <!-- Composer -->
        <div class="border-t border-gray-200 p-3 shrink-0 bg-white">
          <div class="flex items-end gap-2">
            <textarea
              ref="input"
              v-model="draft"
              rows="2"
              maxlength="4000"
              :placeholder="support.active ? 'Write a reply…' : 'What\'s going on?'"
              @keydown="onKeydown"
              class="flex-1 text-sm rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-400 resize-none"
            />
            <button
              type="button"
              @click="send"
              :disabled="sending || !draft.trim()"
              class="px-3 py-2 rounded-lg bg-gray-900 text-white text-sm font-semibold hover:bg-gray-800 disabled:opacity-40 transition"
            >
              {{ sending ? '…' : 'Send' }}
            </button>
          </div>
          <p v-if="error" class="mt-1.5 text-xs text-red-600">{{ error }}</p>
          <p v-else class="mt-1.5 text-[11px] text-gray-400 truncate">
            {{ authStore.displayName }}<span v-if="authStore.user?.tenant_name"> · {{ authStore.user.tenant_name }}</span>
            · {{ pageLabel }}
          </p>
        </div>
      </template>
    </div>
  </transition>
</template>
